/**
 * Schema Parser
 *
 * Parses JSON Schema to extract fields, dependencies, and conditional branches.
 * It is responsible for understanding the schema structure and building
 * the foundational data structures needed by other agents.
 */

import { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import {
  FieldPath,
  DependencyGraph,
  ParsedSchema,
  ConditionalBranch,
  DependencyManagerConfig,
} from './types';

/**
 * Parses a JSON Schema and extracts all fields and their relationships
 *
 * @param schema - The JSON Schema to parse
 * @param config - Dependency manager configuration
 * @returns Parsed schema information
 */
export function parseSchema(
  schema: JSONSchema7,
  config: DependencyManagerConfig,
): ParsedSchema {
  const fields = new Set<FieldPath>();
  const dependencies: DependencyGraph = {};
  const conditionalBranches: ConditionalBranch[] = [];

  function buildFieldPath(segments: string[]): FieldPath {
    return segments.join(config.idSeparator);
  }

  /**
   * Extracts dependencies from UI schema properties
   * Handles custom pickers like EntitySelectionPicker, EntityPicker, etc.
   */
  function extractUiSchemaDependencies(
    property: JSONSchema7,
    fieldPath: FieldPath,
  ): void {
    // Cast to any to access custom UI schema properties
    const prop = property as any;

    // dependsOn: Explicit dependency declaration (in ui:options)
    // Example: { "ui:options": { "dependsOn": ['domain', 'name'] } }
    const uiOptions = prop['ui:options'];
    if (uiOptions?.dependsOn && Array.isArray(uiOptions.dependsOn)) {
      uiOptions.dependsOn.forEach((depField: string) => {
        if (!dependencies[depField]) {
          dependencies[depField] = [];
        }
        if (!dependencies[depField].includes(fieldPath)) {
          dependencies[depField].push(fieldPath);
        }
      });
    }

    // EntitySelectionPicker: ui:fieldName specifies the field this picker depends on
    if (prop['ui:fieldName']) {
      const dependencyField = prop['ui:fieldName'] as string;
      if (!dependencies[dependencyField]) {
        dependencies[dependencyField] = [];
      }
      if (!dependencies[dependencyField].includes(fieldPath)) {
        dependencies[dependencyField].push(fieldPath);
      }
    }

    // EntityPicker: ui:filter specifies fields this picker depends on
    if (prop['ui:filter'] && Array.isArray(prop['ui:filter'])) {
      prop['ui:filter'].forEach((filter: any) => {
        if (filter && filter.fieldName) {
          const dependencyField = filter.fieldName;
          if (!dependencies[dependencyField]) {
            dependencies[dependencyField] = [];
          }
          if (!dependencies[dependencyField].includes(fieldPath)) {
            dependencies[dependencyField].push(fieldPath);
          }
        }
      });
    }

    // EntityRelationsPicker: ui:fieldName specifies the field this picker depends on
    if (prop['ui:field'] === 'EntityRelationsPicker' && prop['ui:fieldName']) {
      const dependencyField = prop['ui:fieldName'] as string;
      if (!dependencies[dependencyField]) {
        dependencies[dependencyField] = [];
      }
      if (!dependencies[dependencyField].includes(fieldPath)) {
        dependencies[dependencyField].push(fieldPath);
      }
    }
  }

  function traverseSchema(
    currentSchema: JSONSchema7 | JSONSchema7Definition,
    pathSegments: string[] = [],
  ): void {
    if (typeof currentSchema === 'boolean') {
      return;
    }

    // Extract properties as fields
    if (currentSchema.properties) {
      Object.keys(currentSchema.properties).forEach(key => {
        const newPath = [...pathSegments, key];
        const fieldPath = buildFieldPath(newPath);
        fields.add(fieldPath);

        const property = currentSchema.properties![key];
        if (typeof property !== 'boolean') {
          // Extract UI schema dependencies (ui:fieldName, ui:filter, etc.)
          extractUiSchemaDependencies(property, fieldPath);

          traverseSchema(property, newPath);
        }
      });
    }

    // Handle dependencies
    if (currentSchema.dependencies) {
      Object.entries(currentSchema.dependencies).forEach(([key, dep]) => {
        const fieldPath = buildFieldPath([...pathSegments, key]);

        if (Array.isArray(dep)) {
          // Array form: field depends on these other fields being present
          const dependentFields = dep.map(d =>
            buildFieldPath([...pathSegments, d]),
          );
          dependencies[fieldPath] = [
            ...(dependencies[fieldPath] || []),
            ...dependentFields,
          ];
        } else if (typeof dep === 'object') {
          // Schema form: when field is present, these fields become required
          const depSchema = dep as JSONSchema7;
          if (depSchema.properties) {
            const dependentFields = Object.keys(depSchema.properties).map(k =>
              buildFieldPath([...pathSegments, k]),
            );
            dependencies[fieldPath] = [
              ...(dependencies[fieldPath] || []),
              ...dependentFields,
            ];
          }

          // Handle oneOf/anyOf dependency form:
          // dependencies: { fieldA: { oneOf: [{ properties: { fieldA: {const: v}, fieldB: {...} } }] } }
          // In this case dep.properties is undefined, but the conditionals branches define dependents.
          const depBranches = depSchema.oneOf || depSchema.anyOf || [];
          if (depBranches.length > 0) {
            const dependentFields = new Set<FieldPath>();
            depBranches.forEach(branch => {
              if (typeof branch !== 'boolean' && branch.properties) {
                Object.keys(branch.properties).forEach(k => {
                  // Exclude the trigger field itself (it appears as a const constraint in branches)
                  if (k !== key) {
                    dependentFields.add(buildFieldPath([...pathSegments, k]));
                  }
                });
              }
            });
            if (dependentFields.size > 0) {
              dependencies[fieldPath] = [
                ...(dependencies[fieldPath] || []),
                ...Array.from(dependentFields).filter(
                  f => !(dependencies[fieldPath] || []).includes(f),
                ),
              ];
            }
          }

          // Recursively traverse the dep schema so nested fields and their
          // own dependencies (e.g. engine → greatExpectations inside propertyType)
          // are also registered.
          traverseSchema(depSchema, pathSegments);
        }
      });
    }

    // Handle oneOf
    if (currentSchema.oneOf) {
      conditionalBranches.push({
        type: 'oneOf',
        path: buildFieldPath(pathSegments),
        branches: currentSchema.oneOf.filter(
          s => typeof s !== 'boolean',
        ) as JSONSchema7[],
      });

      currentSchema.oneOf.forEach(subSchema => {
        if (typeof subSchema !== 'boolean') {
          traverseSchema(subSchema, pathSegments);
        }
      });
    }

    // Handle anyOf
    if (currentSchema.anyOf) {
      conditionalBranches.push({
        type: 'anyOf',
        path: buildFieldPath(pathSegments),
        branches: currentSchema.anyOf.filter(
          s => typeof s !== 'boolean',
        ) as JSONSchema7[],
      });

      currentSchema.anyOf.forEach(subSchema => {
        if (typeof subSchema !== 'boolean') {
          traverseSchema(subSchema, pathSegments);
        }
      });
    }

    // Handle allOf
    if (currentSchema.allOf) {
      conditionalBranches.push({
        type: 'allOf',
        path: buildFieldPath(pathSegments),
        branches: currentSchema.allOf.filter(
          s => typeof s !== 'boolean',
        ) as JSONSchema7[],
      });

      currentSchema.allOf.forEach(subSchema => {
        if (typeof subSchema !== 'boolean') {
          traverseSchema(subSchema, pathSegments);
        }
      });
    }

    // Handle if/then/else
    if (currentSchema.if) {
      const branches: JSONSchema7[] = [];
      if (currentSchema.then && typeof currentSchema.then !== 'boolean') {
        branches.push(currentSchema.then);
      }
      if (currentSchema.else && typeof currentSchema.else !== 'boolean') {
        branches.push(currentSchema.else);
      }

      if (branches.length > 0) {
        conditionalBranches.push({
          type: 'if-then-else',
          path: buildFieldPath(pathSegments),
          branches,
        });

        branches.forEach(branch => traverseSchema(branch, pathSegments));
      }

      if (typeof currentSchema.if !== 'boolean') {
        traverseSchema(currentSchema.if, pathSegments);
      }
    }

    // Handle array items
    if (currentSchema.items) {
      if (Array.isArray(currentSchema.items)) {
        // Tuple validation: each position has its own schema
        currentSchema.items.forEach((item, index) => {
          if (typeof item !== 'boolean') {
            traverseSchema(item, [...pathSegments, String(index)]);
          }
        });
      } else if (typeof currentSchema.items !== 'boolean') {
        // List validation: all items share the same schema
        // We use a special marker for array item schemas
        traverseSchema(currentSchema.items, [...pathSegments, '[]']);
      }
    }
  }

  traverseSchema(schema);

  return {
    fields,
    dependencies,
    conditionalBranches,
  };
}

/**
 * Normalizes a field path by removing array indices
 * Example: "dataContract_0_qualityRule" -> "dataContract_[]_qualityRule"
 *
 * @param fieldPath - The field path to normalize
 * @param separator - The separator used in field paths
 * @returns Normalized field path
 */
export function normalizeFieldPath(
  fieldPath: FieldPath,
  separator: string,
): FieldPath {
  const segments = fieldPath.split(separator);
  const normalized = segments.map(seg => {
    // Replace numeric indices with []
    return /^\d+$/.test(seg) ? '[]' : seg;
  });
  return normalized.join(separator);
}
